import { useStackNavigate } from '@/app/navigation/stack-links';
import { StackLink } from '@/app/navigation/StackLink';
import { Viewer } from '@/components/editor/Viewer';
import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import {
  Timeline,
  TimelineAttachmentContent,
  TimelineEntry,
  TimelineEventDescription,
  getTimelineIcon,
  isUserGroupStart,
} from '@/components/Timeline';
import {
  canViewInLightbox,
  Lightbox,
  type LightboxRef,
} from '@/components/ui/Lightbox';
import { useUserMap } from '@/features/users/users.hooks';
import {
  buildList,
  findNode,
  type AddAttachmentEvent,
  type AddGeotagEvent,
  type BuiltList,
  type ExpandProtoEvent,
  type List,
  type ListAddAttachmentPayload,
  type ListEvent,
  type PatchEvent,
  type User,
  type ValueField,
} from '@certhon/models';
import {
  Check,
  ChevronLeft,
  ClipboardList,
  CornerDownRight,
  MapPin,
  Pencil,
  Wrench,
  X,
} from 'lucide-react';
import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { ListProvider, useListContext } from '@/features/lists/context';

// ---------------------------------------------------------------------------
// Panel wrapper -- wraps content in ListProvider
// ---------------------------------------------------------------------------

export const ChecklistActivityPanel = ({
  params,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const checklistId = params.checklistId || params.templateId;
  const ticketsStore = useCreateTicketStore();

  if (!checklistId) {
    return (
      <Panel {...panelProps} title="Activity">
        <p className="text-muted-foreground text-sm">No checklist selected.</p>
      </Panel>
    );
  }

  return (
    <TicketsStoreProvider store={ticketsStore}>
      <ListProvider listId={checklistId}>
        <ActivityPanelContent basePath={basePath} {...panelProps} />
      </ListProvider>
    </TicketsStoreProvider>
  );
};

// ---------------------------------------------------------------------------
// Aggregated timeline event type
// ---------------------------------------------------------------------------

interface AggregatedEvent {
  id: string;
  type:
    | 'batch-answers'
    | 'fail'
    | 'comment'
    | 'upload'
    | 'geotag'
    | 'list-insert'
    | 'status-change'
    | 'created'
    | 'structural-change'
    | 'metadata-change';
  userId: number;
  summary: string;
  /** Linked entity appended to the summary (e.g. the inserted template). */
  summaryLink?: { to: string; label: string };
  detail?: string;
  /** Rich markdown content for comments (rendered via Viewer) */
  richDetail?: string;
  /** Attachment payloads for upload events (image/video previews + lightbox). */
  attachments?: ListAddAttachmentPayload['attachment'][];
  count?: number;
  timestamp: Date;
  /** True for events sourced from a proto/template that this list expanded. */
  isInherited?: boolean;
  /** Name of the proto list these inherited events originated from. */
  originName?: string;
  /** Route of the proto list these inherited events originated from. */
  originTo?: string;
}

function protoRoute(proto: List): string {
  return proto.werk_id == null
    ? `/templates/${proto.id}`
    : `/lists/${proto.id}`;
}

// ---------------------------------------------------------------------------
// Event aggregation logic
// ---------------------------------------------------------------------------

const BATCH_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function aggregateEvents(
  events: ListEvent[],
  builtList: BuiltList,
  allLists: List[],
): AggregatedEvent[] {
  const primary = aggregateRaw(events, builtList, allLists);

  // Inherited events: changes made to a proto AFTER it was expanded into this
  // list, up until this list was closed. Surface them subtly so consumers see
  // upstream activity without confusing it with their own events.
  const inherited: AggregatedEvent[] = [];
  const closedAt = getClosedAt(events, builtList);
  const expandProtos = events.filter(
    (e): e is ExpandProtoEvent => e.type === 'EXPAND_PROTO',
  );
  for (const epe of expandProtos) {
    const proto = allLists.find((l) => l.id === epe.payload.protoListId);
    if (!proto) continue;
    const start = epe.created.getTime();
    const protoEvents = proto.events.filter((e) => {
      const t = e.created.getTime();
      return t > start && t <= closedAt;
    });
    if (protoEvents.length === 0) continue;
    let protoBuilt: BuiltList;
    try {
      protoBuilt = buildList(proto, allLists);
    } catch {
      continue;
    }
    const aggregated = aggregateRaw(protoEvents, protoBuilt, allLists);
    for (const a of aggregated) {
      inherited.push({
        ...a,
        id: `inherited:${epe.id}:${a.id}`,
        isInherited: true,
        originName: proto.name,
        originTo: protoRoute(proto),
      });
    }
  }

  return [...primary, ...inherited].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );
}

function getClosedAt(events: ListEvent[], builtList: BuiltList): number {
  if (builtList.status !== 'closed') return Infinity;
  let latest = 0;
  for (const e of events) {
    if (e.type === 'CLOSE_LIST') {
      const t = e.created.getTime();
      if (t > latest) latest = t;
    }
  }
  return latest || Infinity;
}

function aggregateRaw(
  events: ListEvent[],
  builtList: BuiltList,
  allLists: List[],
): AggregatedEvent[] {
  // Sort most recent first
  const sorted = [...events].sort(
    (a, b) => b.created.getTime() - a.created.getTime(),
  );

  const result: AggregatedEvent[] = [];

  let i = 0;
  while (i < sorted.length) {
    const event = sorted[i];

    switch (event.type) {
      case 'CREATE_LIST': {
        result.push({
          id: event.id,
          type: 'created',
          userId: event.user_id,
          summary: 'created the checklist',
          timestamp: event.created,
        });
        i++;
        break;
      }

      case 'CLOSE_LIST': {
        result.push({
          id: event.id,
          type: 'status-change',
          userId: event.user_id,
          summary: 'marked the checklist as completed',
          timestamp: event.created,
        });
        i++;
        break;
      }

      case 'OPEN_LIST': {
        result.push({
          id: event.id,
          type: 'status-change',
          userId: event.user_id,
          summary: 'reopened the checklist',
          timestamp: event.created,
        });
        i++;
        break;
      }

      case 'ADD_NOTE': {
        const node = findNode(builtList.tree, event.payload.targetNodeId);
        const fieldTitle = getFieldTitle(node);
        result.push({
          id: event.id,
          type: 'comment',
          userId: event.user_id,
          summary: fieldTitle ? `commented on ${fieldTitle}` : 'commented',
          richDetail: event.payload.note,
          timestamp: event.created,
        });
        i++;
        break;
      }

      case 'ADD_ATTACHMENT': {
        // Batch consecutive attachment uploads by the same user within the time window
        const attachEvents: AddAttachmentEvent[] = [
          event as AddAttachmentEvent,
        ];
        let j = i + 1;
        while (j < sorted.length) {
          const next = sorted[j];
          if (
            next.type === 'ADD_ATTACHMENT' &&
            next.user_id === event.user_id &&
            Math.abs(event.created.getTime() - next.created.getTime()) <=
              BATCH_WINDOW_MS
          ) {
            attachEvents.push(next as AddAttachmentEvent);
            j++;
          } else {
            break;
          }
        }

        // Check if all attachments target the same field
        const firstTarget = attachEvents[0].payload.targetNodeId;
        const allSameField = attachEvents.every(
          (e) => e.payload.targetNodeId === firstTarget,
        );
        const node = allSameField
          ? findNode(builtList.tree, firstTarget)
          : undefined;
        const fieldTitle = getFieldTitle(node);

        const count = attachEvents.length;
        const noun = count === 1 ? 'attachment' : 'attachments';
        result.push({
          id: event.id,
          type: 'upload',
          userId: event.user_id,
          summary: fieldTitle
            ? `uploaded ${count} ${noun} on`
            : `uploaded ${count} ${noun}`,
          detail: fieldTitle ?? undefined,
          attachments: attachEvents.map((e) => e.payload.attachment),
          count,
          timestamp: event.created,
        });
        i = j;
        break;
      }

      case 'ADD_GEOTAG': {
        const geoEvent = event as AddGeotagEvent;
        const node = findNode(builtList.tree, geoEvent.payload.targetNodeId);
        const fieldTitle = getFieldTitle(node);
        result.push({
          id: geoEvent.id,
          type: 'geotag',
          userId: geoEvent.user_id,
          summary: fieldTitle ? `added geotag on` : 'added geotag',
          detail: fieldTitle ?? undefined,
          timestamp: geoEvent.created,
        });
        i++;
        break;
      }

      case 'EXPAND_PROTO': {
        const proto = allLists.find((l) => l.id === event.payload.protoListId);
        const kind = proto && proto.werk_id == null ? 'template' : 'list';
        result.push({
          id: event.id,
          type: 'list-insert',
          userId: event.user_id,
          summary: proto ? `inserted ${kind}` : 'inserted template',
          summaryLink: proto
            ? { to: protoRoute(proto), label: proto.name }
            : undefined,
          timestamp: event.created,
        });
        i++;
        break;
      }

      case 'PATCH': {
        const patchEvent = event as PatchEvent;
        const keys = Object.keys(patchEvent.payload.partialNode);
        const hasValue = keys.includes('value');

        if (!hasValue) {
          // Non-value PATCH (title, description, type, unit, etc.)
          // → treat as structural change; batched below
          const batch = collectStructuralBatch(sorted, i);
          result.push(makeStructuralEntry(batch.events));
          i = batch.nextIndex;
          break;
        }

        const value = patchEvent.payload.partialNode.value;

        // Fails always rendered individually for red-flag visibility
        if (value === false) {
          const node = findNode(
            builtList.tree,
            patchEvent.payload.targetNodeId,
          );
          const fieldTitle = getFieldTitle(node);
          result.push({
            id: patchEvent.id,
            type: 'fail',
            userId: patchEvent.user_id,
            summary: fieldTitle ? 'marked as fail on' : 'marked as fail',
            detail: fieldTitle ?? undefined,
            timestamp: patchEvent.created,
          });
          i++;
          break;
        }

        // Batch consecutive non-fail value-PATCHes by same user within window
        const batch: PatchEvent[] = [patchEvent];
        let j = i + 1;
        while (j < sorted.length) {
          const next = sorted[j];
          if (
            next.type === 'PATCH' &&
            next.user_id === patchEvent.user_id &&
            'partialNode' in next.payload &&
            Object.keys(next.payload.partialNode).includes('value') &&
            next.payload.partialNode.value !== false &&
            Math.abs(patchEvent.created.getTime() - next.created.getTime()) <=
              BATCH_WINDOW_MS
          ) {
            batch.push(next as PatchEvent);
            j++;
          } else {
            break;
          }
        }

        if (batch.length === 1) {
          const node = findNode(
            builtList.tree,
            patchEvent.payload.targetNodeId,
          );
          const fieldTitle = getFieldTitle(node);
          const verb = describeValuePatch(node?.type, value);
          result.push({
            id: patchEvent.id,
            type: 'batch-answers',
            userId: patchEvent.user_id,
            summary: fieldTitle ? `${verb} on` : verb,
            detail: fieldTitle ?? undefined,
            timestamp: patchEvent.created,
          });
        } else {
          result.push(makeBatchAnswerEntry(batch, builtList));
        }
        i = j;
        break;
      }

      case 'INSERT':
      case 'REMOVE':
      case 'MOVE':
      case 'COPY': {
        const batch = collectStructuralBatch(sorted, i);
        result.push(makeStructuralEntry(batch.events));
        i = batch.nextIndex;
        break;
      }

      case 'MODIFY_LIST': {
        const fields = Object.keys(event.payload ?? {});
        result.push({
          id: event.id,
          type: 'metadata-change',
          userId: event.user_id,
          summary:
            fields.length === 0
              ? 'updated list metadata'
              : `updated ${fields.join(', ')}`,
          timestamp: event.created,
        });
        i++;
        break;
      }

      default: {
        // Annotation edits and other low-noise events -- skip
        i++;
        break;
      }
    }
  }

  return result;
}

/**
 * Group consecutive structural events (INSERT/REMOVE/MOVE/COPY + non-value
 * PATCH) by the same user within BATCH_WINDOW_MS into a single entry.
 */
function collectStructuralBatch(
  sorted: ListEvent[],
  startIndex: number,
): { events: ListEvent[]; nextIndex: number } {
  const head = sorted[startIndex];
  const batch: ListEvent[] = [head];
  let j = startIndex + 1;
  while (j < sorted.length) {
    const next = sorted[j];
    if (
      next.user_id !== head.user_id ||
      Math.abs(head.created.getTime() - next.created.getTime()) >
        BATCH_WINDOW_MS
    )
      break;
    if (!isStructuralEvent(next)) break;
    batch.push(next);
    j++;
  }
  return { events: batch, nextIndex: j };
}

function isStructuralEvent(event: ListEvent): boolean {
  switch (event.type) {
    case 'INSERT':
    case 'REMOVE':
    case 'MOVE':
    case 'COPY':
      return true;
    case 'PATCH': {
      const keys = Object.keys((event as PatchEvent).payload.partialNode);
      // value-only PATCH = fill-in answer, not structural
      return !(keys.length === 1 && keys[0] === 'value');
    }
    default:
      return false;
  }
}

function makeStructuralEntry(events: ListEvent[]): AggregatedEvent {
  const head = events[0];
  const counts = { added: 0, removed: 0, moved: 0, edited: 0 };
  for (const e of events) {
    if (e.type === 'INSERT' || e.type === 'COPY') counts.added++;
    else if (e.type === 'REMOVE') counts.removed++;
    else if (e.type === 'MOVE') counts.moved++;
    else if (e.type === 'PATCH') counts.edited++;
  }
  const parts: string[] = [];
  if (counts.added) parts.push(`added ${counts.added}`);
  if (counts.removed) parts.push(`removed ${counts.removed}`);
  if (counts.moved) parts.push(`moved ${counts.moved}`);
  if (counts.edited) parts.push(`edited ${counts.edited}`);
  const summary = `${parts.join(', ')} field${events.length === 1 ? '' : 's'}`;
  return {
    id: head.id,
    type: 'structural-change',
    userId: head.user_id,
    summary,
    count: events.length,
    timestamp: head.created,
  };
}

function getFieldTitle(node: ReturnType<typeof findNode>): string | null {
  if (!node) return null;
  const valueNode = node as ValueField;
  // Prefer English title, fall back to Dutch
  return valueNode.title_en || valueNode.title_nl || null;
}

/** Type-aware verb for a single value PATCH. */
function describeValuePatch(
  fieldType: string | undefined,
  value: unknown,
): string {
  if (fieldType === 'CHECKPOINT_FIELD') {
    if (value === true) return 'marked as pass';
    if (value === false) return 'marked as fail';
    return 'cleared check';
  }
  if (fieldType === 'TASK_FIELD') {
    if (value === true) return 'completed';
    if (value === null) return 'reverted';
    return 'updated task';
  }
  if (fieldType === 'NUMERIC_FIELD') {
    if (value === null) return 'cleared measurement';
    return `entered ${value}`;
  }
  // Unknown type fallback
  if (value === true) return 'marked as pass';
  if (value === false) return 'marked as fail';
  if (value === null) return 'cleared answer';
  return `entered ${value}`;
}

/** Multi-event batch summary: count by field type. */
function makeBatchAnswerEntry(
  batch: PatchEvent[],
  builtList: BuiltList,
): AggregatedEvent {
  const counts = { check: 0, task: 0, measurement: 0, other: 0 };
  for (const e of batch) {
    const node = findNode(builtList.tree, e.payload.targetNodeId);
    switch (node?.type) {
      case 'CHECKPOINT_FIELD':
        counts.check++;
        break;
      case 'TASK_FIELD':
        counts.task++;
        break;
      case 'NUMERIC_FIELD':
        counts.measurement++;
        break;
      default:
        counts.other++;
    }
  }
  const parts: string[] = [];
  if (counts.check)
    parts.push(`${counts.check} check${counts.check === 1 ? '' : 's'}`);
  if (counts.task)
    parts.push(`${counts.task} task${counts.task === 1 ? '' : 's'}`);
  if (counts.measurement)
    parts.push(
      `${counts.measurement} measurement${counts.measurement === 1 ? '' : 's'}`,
    );
  if (counts.other)
    parts.push(`${counts.other} answer${counts.other === 1 ? '' : 's'}`);
  return {
    id: batch[0].id,
    type: 'batch-answers',
    userId: batch[0].user_id,
    summary: `performed ${parts.join(', ')}`,
    count: batch.length,
    timestamp: batch[0].created,
  };
}

// ---------------------------------------------------------------------------
// Icon mapping
// ---------------------------------------------------------------------------

/** Aggregated event types mapped to icons via the shared lookup with overrides. */
const activityIconOverrides: Record<string, typeof Check> = {
  fail: X,
  comment: getTimelineIcon('ADD_NOTE'),
  upload: getTimelineIcon('ADD_ATTACHMENT'),
  geotag: MapPin,
  'list-insert': getTimelineIcon('INSERT'),
  'status-change': ClipboardList,
  created: ClipboardList,
  'batch-answers': Check,
  'structural-change': Wrench,
  'metadata-change': Pencil,
};

function getActivityEventIcon(type: AggregatedEvent['type']) {
  return getTimelineIcon(type, activityIconOverrides);
}

// ---------------------------------------------------------------------------
// Panel content
// ---------------------------------------------------------------------------

function ActivityPanelContent({
  basePath,
  ...panelProps
}: Omit<RoutedPanelProps, 'params'>) {
  const { list, builtList, allLists } = useListContext();
  const navigate = useStackNavigate();
  const userMap = useUserMap<'recnum'>('recnum');
  const lightboxRef = useRef<LightboxRef>(null);

  const timeline = useMemo(
    () => aggregateEvents(list.events, builtList, allLists),
    [list.events, builtList, allLists],
  );

  // All viewable attachments across the timeline (for lightbox navigation)
  const lightboxItems = useMemo(
    () =>
      timeline
        .flatMap((e) => e.attachments ?? [])
        .filter((a) => canViewInLightbox(a.contentType))
        .map((a) => ({
          checksum: a.checksum,
          contentType: a.contentType,
          size: a.size,
          filename: a.filename ?? undefined,
        })),
    [timeline],
  );

  const openInLightbox = useCallback(
    (att: { checksum: string }) => lightboxRef.current?.open(att.checksum),
    [],
  );

  // Navigate back to the checklist detail panel
  const handleBack = () => {
    if (basePath) {
      // basePath is the path for this activity panel; go up one level
      const parentPath = basePath.replace(/\/activity$/, '');
      navigate({ to: parentPath });
    }
  };

  return (
    <Panel {...panelProps} title="Activity">
      {/* Back button */}
      <button
        type="button"
        onClick={handleBack}
        className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm
          transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to list
      </button>

      <Timeline emptyMessage="No activity yet.">
        {timeline.map((event, index) => {
          const prevEvent = index > 0 ? timeline[index - 1] : null;
          const isGroupStart = isUserGroupStart(
            event.userId,
            prevEvent?.userId,
          );
          const user = userMap.get(event.userId);

          // Items are newest-first; the chronologically previous (older)
          // event sits at index + 1.
          const previousTimestamp =
            index < timeline.length - 1
              ? timeline[index + 1].timestamp
              : undefined;

          return (
            <ActivityTimelineEntry
              key={event.id}
              event={event}
              previousTimestamp={previousTimestamp}
              user={user}
              isGroupStart={isGroupStart}
              isLast={index === timeline.length - 1}
              onOpenInLightbox={openInLightbox}
            />
          );
        })}
      </Timeline>
      <Lightbox ref={lightboxRef} attachments={lightboxItems} />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Individual aggregated event entry
// ---------------------------------------------------------------------------

function ActivityTimelineEntry({
  event,
  previousTimestamp,
  user,
  isGroupStart,
  isLast,
  onOpenInLightbox,
}: {
  event: AggregatedEvent;
  previousTimestamp?: Date;
  user: User | undefined;
  isGroupStart: boolean;
  isLast: boolean;
  onOpenInLightbox?: (att: { checksum: string }) => void;
}) {
  const Icon = getActivityEventIcon(event.type);
  const isInherited = event.isInherited === true;

  const timelineUser = {
    recnum: user?.recnum ?? event.userId,
    name: user?.name,
    loginname: user?.loginname,
  };

  const userName = user?.name ?? `User ${event.userId}`;

  const icon: ReactNode = (
    <Icon
      className={`h-4 w-4 shrink-0 ${
        isInherited ? 'text-muted-foreground/50' : 'text-muted-foreground/80' }`}
    />
  );

  const description: ReactNode = (
    <TimelineEventDescription userName={userName}>
      {event.summary}
      {event.summaryLink && (
        <>
          {' '}
          <StackLink to={event.summaryLink.to}>
            {event.summaryLink.label}
          </StackLink>
        </>
      )}
    </TimelineEventDescription>
  );

  return (
    <TimelineEntry
      timestamp={event.timestamp}
      previousTimestamp={previousTimestamp}
      user={timelineUser}
      isGroupStart={isGroupStart}
      isLastEvent={isLast}
      icon={icon}
      description={description}
      contentClassName={isInherited ? 'opacity-60 italic' : undefined}
    >
      {isInherited && event.originName && (
        <p className="text-muted-foreground/70 mt-0.5 ml-6 flex items-center gap-1 text-[11px]">
          <CornerDownRight className="h-3 w-3" />
          from{' '}
          {event.originTo ? (
            <StackLink to={event.originTo}>{event.originName}</StackLink>
          ) : (
            event.originName
          )}
        </p>
      )}
      {event.detail && (
        <p className="text-muted-foreground mt-0.5 ml-6 text-xs">
          {event.detail}
        </p>
      )}
      {event.richDetail && (
        <div className="mt-1 ml-6">
          <Viewer value={event.richDetail} className="text-xs" />
        </div>
      )}
      {event.attachments && event.attachments.length > 0 && (
        <div className="mt-1 ml-6 flex flex-wrap gap-2">
          {event.attachments.map((att) => (
            <TimelineAttachmentContent
              key={att.checksum}
              checksum={att.checksum}
              contentType={att.contentType}
              size={att.size}
              filename={att.filename}
              onOpenInLightbox={onOpenInLightbox}
            />
          ))}
        </div>
      )}
    </TimelineEntry>
  );
}
